import { useState, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'

const MAX_ATTEMPTS = 5
const LOCKOUT_MS = 15 * 60 * 1000 // 15 minutes

export default function Login() {
  const { login } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Rate limiting state
  const [attempts, setAttempts] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('login_attempts') || 'null')
      if (stored && Date.now() - stored.firstAttempt > LOCKOUT_MS) {
        localStorage.removeItem('login_attempts')
        return { count: 0, firstAttempt: null }
      }
      return stored || { count: 0, firstAttempt: null }
    } catch {
      return { count: 0, firstAttempt: null }
    }
  })

  const isLocked = attempts.count >= MAX_ATTEMPTS
  const remainingTime = isLocked && attempts.firstAttempt
    ? Math.ceil((LOCKOUT_MS - (Date.now() - attempts.firstAttempt)) / 60000)
    : 0

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    setError('')

    if (isLocked) {
      setError(`تم تجاوز عدد المحاولات. حاول بعد ${remainingTime} دقيقة`)
      return
    }

    setLoading(true)

    const result = await login(username, password)

    if (!result.success) {
      // Track failed attempt
      const newAttempts = {
        count: attempts.count + 1,
        firstAttempt: attempts.firstAttempt || Date.now(),
      }
      setAttempts(newAttempts)
      localStorage.setItem('login_attempts', JSON.stringify(newAttempts))

      if (newAttempts.count >= MAX_ATTEMPTS) {
        setError('تم تجاوز عدد المحاولات المسموحة. حاول بعد 15 دقيقة')
      } else {
        const remaining = MAX_ATTEMPTS - newAttempts.count
        setError(`${result.error} (تبقى ${remaining} ${remaining === 1 ? 'محاولة' : 'محاولات'})`)
      }
    } else {
      // Success — clear attempts
      localStorage.removeItem('login_attempts')
    }

    setLoading(false)
  }, [username, password, login, isLocked, remainingTime, attempts])

  return (
    <div className="min-h-screen bg-void flex items-center justify-center p-4" dir="rtl">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-surface border border-white/[0.06] flex items-center justify-center text-2xl font-bold text-[#8B5CF6] mx-auto mb-4">
            س
          </div>
          <h1 className="text-2xl font-bold text-[#F1F5F9]">مركز سناجم القيادي</h1>
          <p className="text-sm text-[#64748B] mt-1">تسجيل الدخول للمتابعة</p>
        </div>

        {/* Login card */}
        <form onSubmit={handleSubmit} className="relative overflow-hidden rounded-[20px] bg-surface border border-white/[0.06] p-6">
          {/* Accent strip */}
          <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-l from-[#C084FC] to-[#8B5CF6] opacity-80" />

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium">
              {error}
            </div>
          )}

          {isLocked && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm font-medium">
              ⚠️ تم قفل الحساب مؤقتاً. حاول بعد {remainingTime} دقيقة
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-[#94A3B8] mb-2">اسم المستخدم</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-[10px] bg-base border border-white/[0.06] text-[#F1F5F9] placeholder-[#475569] focus:border-[#8B5CF6]/50 focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]/30 transition-all duration-150 text-sm"
              placeholder=""
              dir="ltr"
              autoComplete="username"
              disabled={isLocked}
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-[#94A3B8] mb-2">كلمة المرور</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-[10px] bg-base border border-white/[0.06] text-[#F1F5F9] placeholder-[#475569] focus:border-[#8B5CF6]/50 focus:outline-none focus:ring-1 focus:ring-[#8B5CF6]/30 transition-all duration-150 text-sm"
              placeholder=""
              dir="ltr"
              autoComplete="current-password"
              disabled={isLocked}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || isLocked}
            className="w-full py-3 rounded-[10px] bg-gradient-to-l from-[#8B5CF6] to-[#6D28D9] text-white font-semibold text-sm hover:opacity-90 transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                جارٍ الدخول...
              </span>
            ) : isLocked ? (
              'الحساب مقفل مؤقتاً'
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </form>

        {/* Attempt counter */}
        {!isLocked && attempts.count > 0 && (
          <p className="text-center text-xs text-[#475569] mt-3">
            {MAX_ATTEMPTS - attempts.count} {MAX_ATTEMPTS - attempts.count === 1 ? 'محاولة متبقية' : 'محاولات متبقية'}
          </p>
        )}

        <p className="text-center text-xs text-[#475569] mt-6">
          © 2026 Sanajim Technologies
        </p>
      </div>
    </div>
  )
}
